import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Lock, Unlock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Effort & cost lock badge + control. Self-fetches /lock (which carries
 * `can_manage` for owner / CCO / CRO / Super Admin) and reports changes up.
 */
export function EstimateLockControl({ estimateId, onChange, compact = false, refreshKey = null }) {
  const [lock, setLock] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!estimateId) return;
    let alive = true;
    axios.get(`${API_URL}/api/estimates/${estimateId}/lock`, { withCredentials: true })
      .then((r) => { if (alive) { setLock(r.data); onChange?.(r.data); } })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateId, refreshKey]);

  const apply = async (locked) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/estimates/${estimateId}/lock`, { locked, reason: reason.trim() || null }, { withCredentials: true });
      setLock(r.data);
      onChange?.(r.data);
      setOpen(false);
      setReason('');
      toast.success(locked ? 'Effort & cost locked — plan changes remain editable' : 'Effort & cost unlocked');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update lock');
    } finally { setBusy(false); }
  };

  if (!lock) return null;
  const when = lock.at ? new Date(lock.at).toLocaleDateString() : '';

  if (lock.locked) {
    return (
      <span className="inline-flex items-center gap-1.5 align-middle" data-testid="estimate-lock-badge">
        <span
          className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-800 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded"
          title={`Effort & cost locked by ${lock.by_name || lock.by_email || '—'}${when ? ` on ${when}` : ''}${lock.reason ? ` — ${lock.reason}` : ''}. Plan changes stay editable.`}
          data-testid="estimate-lock-badge-label"
        >
          <Lock className="w-3 h-3" /> {compact ? 'Locked' : `Effort & cost ${lock.auto ? 'auto-locked' : 'locked'} · ${lock.by_name || lock.by_email || '—'}`}
        </span>
        {lock.can_manage && (
          <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-amber-800 hover:bg-amber-50" onClick={() => apply(false)} disabled={busy} data-testid="estimate-unlock-btn">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Unlock className="w-3 h-3 mr-1" /> Unlock</>}
          </Button>
        )}
      </span>
    );
  }

  if (!lock.can_manage) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 align-middle text-[10px] font-semibold text-slate-500 hover:text-amber-800 bg-white hover:bg-amber-50 border border-slate-200 hover:border-amber-300 px-2 py-0.5 rounded transition-colors"
          title="Freeze effort & cost so only plan changes can be saved"
          data-testid="estimate-lock-btn"
        >
          <Lock className="w-3 h-3" /> Lock effort & cost
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 space-y-2" data-testid="estimate-lock-popover">
        <p className="text-sm font-semibold text-[#0F2040]">Lock effort & cost</p>
        <p className="text-xs text-slate-500">Saves that change effort or cost will be rejected until unlocked. Plan, schedule, name and lead changes stay editable. Only the owner, CCO, CRO or Super Admin can unlock.</p>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value.slice(0, 300))} rows={2} placeholder="Reason (optional) — e.g. Quote issued to client" className="text-xs resize-none" data-testid="estimate-lock-reason-input" />
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpen(false)} data-testid="estimate-lock-cancel-btn">Cancel</Button>
          <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white" onClick={() => apply(true)} disabled={busy} data-testid="estimate-lock-confirm-btn">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Lock className="w-3 h-3 mr-1" /> Lock</>}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
